// ─── Design Tokens (Clean, Vibrant Palette) ──────────────────────────────────
export const T = {
  blue: "#2563EB",   // Vibrant Main Blue
  green: "#10B981",  // Emerald Success Green
  violet: "#8B5CF6", // Organic Purple
  amber: "#F59E0B",  // PID Orange
  rose: "#E11D48",   // Vivid Red for Failed
  cyan: "#06B6D4",   // Bright Cyan
  teal: "#0D9488",   // Deep Teal
  indigo: "#4F46E5", // Deep Indigo
};

// Colors for Pie and Bar charts
export const PALETTE = [T.blue, T.amber, T.violet, T.green, T.cyan, T.rose, T.teal, T.indigo];

// ─── Data builders ────────────────────────────────────────────────────────────
export const buildTimelineData = (data) => {
  const map = {};
  data.forEach((p) => {
    const day = (p.createdAt || "").slice(0, 10);
    if (!day) return;
    if (!map[day]) map[day] = { date: day, total: 0, success: 0, failed: 0, revenue: 0, currencyBreakdown: {} };
    map[day].total += 1;
    if (p.p_status === "success") {
      map[day].success += 1;
      map[day].revenue += p.p_amount || 0;
      const cur = p.p_currency || "UNKNOWN";
      map[day].currencyBreakdown[cur] = (map[day].currencyBreakdown[cur] || 0) + (p.p_amount || 0);
    } else {
      map[day].failed += 1;
    }
  });
  return Object.values(map).sort((a, b) => a.date.localeCompare(b.date))
    .map((d) => ({ ...d, date: d.date.slice(5), revenue: parseFloat(d.revenue.toFixed(2)) }));
};

export const buildTypeData = (data) => {
  const map = {};
  data.forEach((p) => {
    const t = p.p_type || "unknown";
    if (!map[t]) map[t] = { name: t.toUpperCase(), value: 0 };
    map[t].value += 1;
  });
  return Object.values(map);
};

export const buildCampaignData = (data) => {
  const map = {};
  data.forEach((p) => {
    const c = p.p_campaign || "Direct";
    if (!map[c]) map[c] = { campaign: c, purchases: 0, revenue: 0, currencyBreakdown: {} };
    map[c].purchases += 1;
    const cur = p.p_currency || "UNKNOWN";
    map[c].currencyBreakdown[cur] = (map[c].currencyBreakdown[cur] || 0) + (p.p_amount || 0);
    map[c].revenue += p.p_amount || 0;
  });
  return Object.values(map).sort((a, b) => b.purchases - a.purchases).slice(0, 8)
    .map((d) => ({ ...d, revenue: parseFloat(d.revenue.toFixed(2)) }));
};

export const buildProductData = (data) => {
  const map = {};
  data.forEach((p) => {
    const name = p.p_name || "Unknown";
    const cur = p.p_currency || "UNKNOWN";
    if (!map[name]) map[name] = { product: name, revenue: 0, currencyBreakdown: {} };
    map[name].revenue += p.p_amount || 0;
    map[name].currencyBreakdown[cur] = (map[name].currencyBreakdown[cur] || 0) + (p.p_amount || 0);
  });
  return Object.values(map).sort((a, b) => b.revenue - a.revenue).slice(0, 6)
    .map((d) => ({ ...d, revenue: parseFloat(d.revenue.toFixed(2)) }));
};

// ════════════════════════════════════════════════════════════════════════════
// ─── ECharts option builders ──────────────────────────────────────────────────
// ════════════════════════════════════════════════════════════════════════════

export const buildAreaChartOption = (timelineData, revenueByKey) => ({
  backgroundColor: "transparent",
  tooltip: {
    trigger: "axis",
    axisPointer: { type: "cross", crossStyle: { color: T.blue, width: 1.5 }, lineStyle: { color: T.blue, width: 1.5, type: "dashed" } },
    backgroundColor: "rgba(15,23,42,0.97)",
    borderColor: "rgba(37,99,235,0.45)",
    borderWidth: 1,
    borderRadius: 14,
    padding: [14, 18],
    textStyle: { color: "#f1f5f9", fontSize: 13 },
    formatter: (params) => {
      const label = params[0]?.axisValue || "";
      let html = `<div style="font-size:10px;font-weight:800;letter-spacing:0.1em;text-transform:uppercase;color:#94a3b8;margin-bottom:10px;border-bottom:1px solid rgba(255,255,255,0.07);padding-bottom:8px;">${label}</div>`;
      params.forEach(p => {
        const isRev = p.seriesName === "Revenue";
        const breakdown = isRev && revenueByKey ? revenueByKey[label] : null;
        html += `<div style="display:flex;align-items:center;gap:8px;margin-top:6px;">
          <span style="width:9px;height:9px;border-radius:50%;background:${p.color};display:inline-block;box-shadow:0 0 8px ${p.color};flex-shrink:0;"></span>
          <span style="color:#94a3b8;font-size:12px;font-weight:600;">${p.seriesName}</span>
        </div>`;
        if (isRev && breakdown) {
          html += `<div style="margin-top:4px;padding-left:17px;">`;
          Object.entries(breakdown).forEach(([cur, amt]) => {
            html += `<div style="display:flex;justify-content:space-between;align-items:center;gap:20px;margin-top:4px;">
              <span style="font-size:10px;font-weight:900;letter-spacing:0.08em;color:#94a3b8;background:rgba(255,255,255,0.07);border:1px solid rgba(255,255,255,0.12);border-radius:5px;padding:2px 7px;">${cur}</span>
              <span style="color:#f1f5f9;font-size:13px;font-weight:800;">${parseFloat(amt).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>`;
          });
          html += `</div>`;
        } else {
          html += `<div style="padding-left:17px;margin-top:2px;"><span style="color:#f1f5f9;font-size:14px;font-weight:800;">${typeof p.value === "number" ? p.value.toLocaleString() : p.value}</span></div>`;
        }
      });
      return html;
    }
  },
  legend: { show: false },
  grid: { top: 10, right: 60, bottom: 30, left: 10, containLabel: true },
  xAxis: {
    type: "category",
    data: timelineData.map(d => d.date),
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: { color: "#64748b", fontSize: 11, fontWeight: 600 },
    splitLine: { show: false },
  },
  yAxis: [
    {
      type: "value",
      name: "Purchases",
      position: "left",
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: "#64748b", fontSize: 11 },
      splitLine: { lineStyle: { color: "#e2e8f0", type: "dashed" } },
    },
    {
      type: "value",
      name: "Revenue",
      position: "right",
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: "#64748b", fontSize: 11 },
      splitLine: { show: false },
    },
  ],
  series: [
    {
      name: "Successful",
      type: "line",
      yAxisIndex: 0,
      data: timelineData.map(d => d.success),
      smooth: true,
      symbol: "none",
      lineStyle: { color: T.green, width: 2.5 },
      // Soft clean fade to transparent
      areaStyle: { color: { type: "linear", x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: T.green + "40" }, { offset: 1, color: T.green + "00" }] } },
      emphasis: { focus: "series" },
    },
    {
      name: "Failed",
      type: "line",
      yAxisIndex: 0,
      data: timelineData.map(d => d.failed),
      smooth: true,
      symbol: "none",
      lineStyle: { color: T.rose, width: 2.5 },
      areaStyle: { color: { type: "linear", x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: T.rose + "40" }, { offset: 1, color: T.rose + "00" }] } },
      emphasis: { focus: "series" },
    },
    {
      name: "Revenue",
      type: "line",
      yAxisIndex: 1,
      data: timelineData.map(d => d.revenue),
      smooth: true,
      symbol: "none",
      lineStyle: { color: T.blue, width: 2.5, type: "dashed" },
      areaStyle: { color: { type: "linear", x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: T.blue + "40" }, { offset: 1, color: T.blue + "00" }] } },
      emphasis: { focus: "series" },
    },
  ],
});

export const buildPieChartOption = (typeData) => ({
  backgroundColor: "transparent",
  tooltip: {
    trigger: "item",
    backgroundColor: "rgba(15,23,42,0.97)",
    borderColor: "rgba(37,99,235,0.45)",
    borderWidth: 1,
    borderRadius: 14,
    padding: [12, 16],
    formatter: (p) => `
      <div style="display:flex;align-items:center;gap:8px;">
        <span style="width:9px;height:9px;border-radius:50%;background:${p.color};display:inline-block;box-shadow:0 0 8px ${p.color};"></span>
        <span style="color:#94a3b8;font-size:12px;">${p.name}</span>
        <span style="color:#f1f5f9;font-size:13px;font-weight:800;">${p.value.toLocaleString()}</span>
        <span style="color:#64748b;font-size:11px;">(${p.percent.toFixed(1)}%)</span>
      </div>`,
  },
  legend: { show: false },
  series: [{
    type: "pie",
    radius: ["42%", "76%"],
    center: ["50%", "50%"],
    padAngle: 2,
    data: typeData.map((d, i) => ({
      name: d.name,
      value: d.value,
      itemStyle: {
        // Pure solid vibrant color with a clean white border for a modern SaaS look
        color: PALETTE[i % PALETTE.length],
        borderColor: "#ffffff",
        borderWidth: 2,
      },
    })),
    label: {
      show: true,
      position: "inside",
      formatter: (p) => p.percent >= 5 ? `${p.percent.toFixed(0)}%` : "",
      fontSize: 12,
      fontWeight: 800,
      color: "#fff",
      textShadowBlur: 4,
      textShadowColor: "rgba(0,0,0,0.4)", // Lighter text shadow
    },
    emphasis: { 
      scale: true, 
      scaleSize: 6, 
      itemStyle: { shadowBlur: 10, shadowColor: "rgba(0,0,0,0.15)" } // Very subtle natural drop shadow on hover
    },
    animationType: "expansion",
    animationDuration: 800,
  }],
});

export const buildProductBarOption = (productData) => ({
  backgroundColor: "transparent",
  tooltip: {
    trigger: "axis",
    axisPointer: { type: "shadow" },
    backgroundColor: "rgba(15,23,42,0.97)",
    borderColor: "rgba(37,99,235,0.45)",
    borderWidth: 1,
    borderRadius: 14,
    padding: [12, 16],
    formatter: (params) => {
      const row = params[0];
      const item = productData[row.dataIndex];
      let html = `<div style="font-size:10px;font-weight:800;letter-spacing:0.08em;text-transform:uppercase;color:#94a3b8;margin-bottom:8px;">${row.name}</div>`;
      html += `<div style="display:flex;align-items:center;gap:8px;">
        <span style="width:8px;height:8px;border-radius:50%;background:${row.color};box-shadow:0 0 6px ${row.color};display:inline-block;"></span>
        <span style="color:#94a3b8;font-size:12px;">Revenue</span>
      </div>`;
      if (item?.currencyBreakdown) {
        html += `<div style="margin-top:6px;padding-left:16px;">`;
        Object.entries(item.currencyBreakdown).forEach(([cur, amt]) => {
          html += `<div style="display:flex;justify-content:space-between;gap:16px;margin-top:4px;">
            <span style="font-size:10px;font-weight:800;color:#94a3b8;background:rgba(255,255,255,0.07);border:1px solid rgba(255,255,255,0.12);border-radius:4px;padding:2px 6px;">${cur}</span>
            <span style="color:#f1f5f9;font-size:13px;font-weight:800;">${parseFloat(amt).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>`;
        });
        html += `</div>`;
      }
      return html;
    },
  },
  legend: { show: false },
  grid: { top: 8, right: 24, bottom: 8, left: 8, containLabel: true },
  xAxis: {
    type: "value",
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: { color: "#94a3b8", fontSize: 10 },
    splitLine: { lineStyle: { color: "#e2e8f0", type: "dashed" } },
  },
  yAxis: {
    type: "category",
    data: productData.map(d => d.product.length > 14 ? d.product.slice(0, 14) + "…" : d.product),
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: { color: "#1e293b", fontSize: 11, fontWeight: 700 },
    splitLine: { show: false },
  },
  series: [{
    type: "bar",
    data: productData.map((d, i) => ({
      value: d.revenue,
      itemStyle: {
        // Clean solid color, no muddy shadows
        color: PALETTE[i % PALETTE.length],
        borderRadius: [0, 6, 6, 0],
      },
    })),
    barMaxWidth: 26,
    emphasis: { itemStyle: { color: T.blue } }, // Highlight blue on hover
  }],
});

export const buildCampaignBarOption = (campaignData) => ({
  backgroundColor: "transparent",
  tooltip: {
    trigger: "axis",
    axisPointer: { type: "shadow" },
    backgroundColor: "rgba(15,23,42,0.97)",
    borderColor: "rgba(37,99,235,0.45)",
    borderWidth: 1,
    borderRadius: 14,
    padding: [12, 16],
    formatter: (params) => {
      const label = params[0]?.axisValue || "";
      const item = campaignData.find(d => d.campaign === label);
      let html = `<div style="font-size:10px;font-weight:800;letter-spacing:0.08em;text-transform:uppercase;color:#94a3b8;margin-bottom:8px;">${label}</div>`;
      params.forEach(p => {
        const isRev = p.seriesName === "Revenue";
        html += `<div style="display:flex;align-items:center;gap:8px;margin-top:5px;">
          <span style="width:8px;height:8px;border-radius:50%;background:${p.color};box-shadow:0 0 6px ${p.color};display:inline-block;"></span>
          <span style="color:#94a3b8;font-size:12px;">${p.seriesName}:</span>`;
        if (isRev && item?.currencyBreakdown) {
          html += `<span style="color:#f1f5f9;font-size:12px;font-weight:800;">`;
          html += Object.entries(item.currencyBreakdown)
            .map(([c, a]) => `${c} ${parseFloat(a).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)
            .join(" + ");
          html += `</span>`;
        } else {
          html += `<span style="color:#f1f5f9;font-size:13px;font-weight:800;">${typeof p.value === "number" ? p.value.toLocaleString() : p.value}</span>`;
        }
        html += `</div>`;
      });
      return html;
    },
  },
  legend: {
    bottom: 0,
    icon: "circle",
    itemWidth: 10,
    itemHeight: 10,
    textStyle: { color: "#1e293b", fontSize: 12, fontWeight: 700 },
  },
  grid: { top: 24, right: 60, bottom: 48, left: 10, containLabel: true },
  xAxis: {
    type: "category",
    data: campaignData.map(d => d.campaign),
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: { color: "#475569", fontSize: 11, fontWeight: 700, rotate: -28, interval: 0 },
    splitLine: { show: false },
  },
  yAxis: [
    {
      type: "value",
      name: "Purchases",
      position: "left",
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: "#64748b", fontSize: 11 },
      splitLine: { lineStyle: { color: "#e2e8f0", type: "dashed" } },
    },
    {
      type: "value",
      name: "Revenue",
      position: "right",
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: "#64748b", fontSize: 11 },
      splitLine: { show: false },
    },
  ],
  series: [
    {
      name: "Purchases",
      type: "bar",
      yAxisIndex: 0,
      data: campaignData.map(d => d.purchases),
      barMaxWidth: 32,
      itemStyle: {
        // Clean, solid vibrant blue
        color: T.blue,
        borderRadius: [6, 6, 0, 0],
      },
      emphasis: { itemStyle: { color: "#1d4ed8" } }, // Slightly deeper blue on hover
    },
    {
      name: "Revenue",
      type: "bar",
      yAxisIndex: 1,
      data: campaignData.map(d => d.revenue),
      barMaxWidth: 32,
      itemStyle: {
        // Clean, solid emerald green
        color: T.green,
        borderRadius: [6, 6, 0, 0],
      },
      emphasis: { itemStyle: { color: "#059669" } }, // Slightly deeper green on hover
    },
  ],
});

